import { getTimeElapsed, getRelativeDateType } from '../../../../utils/dateUtils';
import { getTimeUntilShift, calculateLoggedTime, getUnassignedShiftsForEmployee } from '../../../../utils/shiftUtils';

const getPastShiftStatus = (shifts, loggedHours, loggedMinutes) => {
  if (shifts.some((shift) => shift.clockedInAt && shift.clockedOutAt)) {
    return {
      status: 'completed',
      text: `${loggedHours}hr ${loggedMinutes}min logged`,
    };
  }
  return {
    status: 'default',
    text: 'No time logged',
  };
};

const getTodayShiftStatus = (shifts, loggedHours, loggedMinutes, unassignedShiftCount) => {
  const now = new Date();
  const allShiftsComplete = shifts.length > 0 && shifts.every((shift) => new Date(shift.end) < now);

  if (allShiftsComplete) {
    return getPastShiftStatus(shifts, loggedHours, loggedMinutes);
  }

  // Check for ongoing shift
  const currentShift = shifts.find((shift) => {
    const shiftStart = new Date(shift.start);
    const shiftEnd = new Date(shift.end);
    return now >= shiftStart && now <= shiftEnd;
  });

  if (currentShift) {
    const elapsed = getTimeElapsed(currentShift.start);
    return {
      status: 'current',
      text: `Shift started ${elapsed.hours}h ${elapsed.minutes}m ago`,
    };
  }

  // Check for upcoming shift
  const nextShift = shifts.find((shift) => new Date(shift.start) > now);
  if (nextShift) {
    const timeUntil = getTimeUntilShift(nextShift.start);
    return {
      status: 'current',
      text: `Shift starting in ${timeUntil.hours}h ${timeUntil.minutes}m`,
    };
  }

  // Handle unassigned shifts or no shifts
  if (unassignedShiftCount > 0) {
    return {
      status: 'actionRequired',
      text: `${unassignedShiftCount} shift${unassignedShiftCount === 1 ? '' : 's'} available`,
    };
  }

  return {
    status: 'current',
    text: 'No available shifts',
  };
};

const getFutureShiftStatus = (shifts, unassignedShiftCount) => {
  if (shifts.length > 0) {
    return {
      status: 'actionRequired',
      text: `${shifts.length} shift${shifts.length === 1 ? '' : 's'} scheduled`,
    };
  }

  if (unassignedShiftCount > 0) {
    return {
      status: 'actionRequired',
      text: `${unassignedShiftCount} shift${unassignedShiftCount === 1 ? '' : 's'} available`,
    };
  }

  return {
    status: 'default',
    text: 'No available shifts',
  };
};

export const getShiftSubtitle = ({ date, shifts, employee, allShifts }) => {
  const dateType = getRelativeDateType(date);
  const { hours: loggedHours, minutes: loggedMinutes } = calculateLoggedTime(shifts);
  const unassignedShifts = getUnassignedShiftsForEmployee(date, allShifts, employee);
  const unassignedShiftCount = unassignedShifts.length;

  switch (dateType) {
    case 'past':
      return getPastShiftStatus(shifts, loggedHours, loggedMinutes);
    case 'today':
      return getTodayShiftStatus(shifts, loggedHours, loggedMinutes, unassignedShiftCount);
    case 'future':
      return getFutureShiftStatus(shifts, unassignedShiftCount);
    default:
      throw new Error(`Invalid date type: ${dateType}`);
  }
};
